import React, { useState, useRef, useEffect } from "react";
import { askAssistant } from "../../auth/firebaseAuth";
import "./HelpAssistant.css";

const GREETING = {
  role: "assistant",
  content:
    "Hi! I can help with using the board, setting up room screens, and the admin controls (Wi-Fi, connecting a screen, Care Circle, and settings). Ask me anything, or pick a topic below.",
};

const SUGGESTIONS = [
  "How do I connect a room screen to a resident?",
  "How do I set up Wi-Fi on the room tablet?",
  "Where did the settings controls go?",
  "How do I arrange messages so they don't cover the photos?",
  "How do I invite a Care Circle member?",
];

// Floating help assistant (Claude-powered). Instructions-only; no resident data.
export default function HelpAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, busy, open]);

  const send = async (text) => {
    const q = (text || "").trim();
    if (!q || busy) return;
    setError(null);
    // Only real turns go to the server (drop the local greeting).
    const history = messages.filter((m) => m !== GREETING);
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const { text: reply } = await askAssistant([...history, { role: "user", content: q }]);
      setMessages((cur) => [...cur, { role: "assistant", content: reply }]);
    } catch (err) {
      setError("Couldn't reach the assistant. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  if (!open) {
    return (
      <button className="ha-fab" onClick={() => setOpen(true)} aria-label="Open help assistant">
        💬 Help
      </button>
    );
  }

  return (
    <div className="ha-panel" role="dialog" aria-label="Help assistant">
      <div className="ha-head">
        <span className="ha-title">Help assistant</span>
        <button className="ha-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
      </div>

      <div className="ha-log" ref={logRef}>
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "ha-msg ha-user" : "ha-msg ha-bot"}>{m.content}</div>
        ))}
        {messages.length === 1 && (
          <div className="ha-suggest">
            {SUGGESTIONS.map((s) => (
              <button key={s} className="ha-chip" onClick={() => send(s)}>{s}</button>
            ))}
          </div>
        )}
        {busy && <div className="ha-typing">Assistant is typing…</div>}
        {error && <p className="ha-error">{error}</p>}
      </div>

      <div className="ha-foot">
        <p className="ha-note">Tip: don't include residents' personal or health information.</p>
        <div className="ha-inputrow">
          <textarea
            className="ha-input"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask a question…"
          />
          <button className="ha-send" disabled={busy || !input.trim()} onClick={() => send(input)}>Send</button>
        </div>
      </div>
    </div>
  );
}
